/**
 * @fileoverview Transform layer between DocumentTemplateBuilder's internal model and MongoDB schema.
 * @module local-db/reporting/reportTemplateTransform
 *
 * Responsibilities:
 * - Map DocumentConfig (15-section builder model) to ReportTemplateLocal (flat MongoDB schema).
 * - Map ReportTemplateLocal back to DocumentConfig for builder hydration.
 *
 * Dependencies: core-domain reporting types, local-db Dexie types.
 * Architecture: Pure transform logic — no Dexie or API calls.
 * Notes: toReportTemplate() = builder → DB; fromReportTemplate() = DB → builder.
 */
// toReportTemplate()  — builder state → flat ReportTemplateLocal fields
// fromReportTemplate() — flat ReportTemplateLocal → builder DocumentConfig
//
// The builder has UI-specific sections (header, footer, subjectLine,
// salutation, contactBlock, staticContent) that don't map to spec section
// types. These are preserved in the `builderConfig` bag on the
// ReportTemplateLocal so they survive round-trips.
// ---------------------------------------------------------------------------

import type {
  ReportTemplateLocal,
  ReportSection,
  HeaderConfig,
  FooterConfig,
  SignaturesConfig,
  PhotoConfig,
  AutoHideRules,
  LegalTextConfig,
  PermitConfig,
  CoverPageConfig,
  DocumentType,
  ReportDataScope,
  ComplexVisitSummarySectionConfig,
  ComplexParticipantsSectionConfig,
} from './reportTemplateTypes';

// ---------------------------------------------------------------------------
// Builder types (mirrors DocumentTemplateBuilderPage.tsx local types)
// These are not imported directly to avoid circular deps between local-db
// and tenant-web. The shapes are stable — defined in core-domain presets.
// ---------------------------------------------------------------------------

export interface BuilderSectionConfig {
  id: string;
  label: string;
  description?: string;
  enabled: boolean;
  pageBreakBefore?: boolean;
  // icon is React component — stripped before save, not present here
}

export interface BuilderDocumentConfig {
  documentType: 'inspectionReport' | 'permit' | 'noticeOfViolation' | 'letter' | 'certificateOfCompliance' | 'custom';
  reportDataScope?: ReportDataScope;
  layoutMode: 'form' | 'letter';
  pageSize: 'letter' | 'a4' | 'legal';
  orientation: 'portrait' | 'landscape';
  /** Theme preset id (ThemePresetId from document-render). Round-tripped via the builderConfig bag. */
  themePreset?: string;
  /**
   * Custom theme overrides (DocumentThemeCustom: primary/accent/heading/baseFont) layered
   * on top of themePreset (Phase E2). Round-tripped via the builderConfig bag like themePreset.
   */
  themeCustom?: { primary?: string; accent?: string; heading?: string; baseFont?: string };
  approvalMode?: 'autoSend' | 'requireApproval';
  sections: BuilderSectionConfig[];
  header?: Record<string, any>;
  recipientBlock?: Record<string, any>;
  subjectLine?: Record<string, any>;
  salutation?: Record<string, any>;
  bodyText?: Record<string, any>;
  metadata?: Record<string, any>;
  violationConfig?: Record<string, any>;
  photoConfig?: Record<string, any>;
  comments?: Record<string, any>;
  contactBlock?: Record<string, any>;
  signatures?: Record<string, any>;
  legalText?: Record<string, any>;
  footerConfig?: Record<string, any>;
  staticContent?: Record<string, any>;
  coverPage?: Record<string, any>;
  permitConfig?: Record<string, any>;
  complexVisitSummary?: ComplexVisitSummarySectionConfig;
  complexParticipants?: ComplexParticipantsSectionConfig;
}

// ---------------------------------------------------------------------------
// Builder section ID → spec ReportSectionType mapping
// ---------------------------------------------------------------------------
const BUILDER_TO_SPEC_SECTION: Record<string, string | null> = {
  cover:          'coverPage',
  header:         null,          // spec has header as top-level field, not a section
  recipientBlock: 'entityInfo',  // maps to entityInfo + responsibleParty
  subjectLine:    null,          // builder-only (letter mode)
  salutation:     null,          // builder-only (letter mode)
  bodyText:       'customText',
  complexVisitSummary: 'complexVisitSummary',
  complexParticipants: 'complexParticipants',
  metadata:       'inspectionInfo',
  violations:     'violations',
  photos:         'photos',
  comments:       'inspectorNotes',
  contactBlock:   null,          // builder-only
  signatures:     'signatures',
  legal:          'legalText',
  footer:         null,          // spec has footer as top-level field
  staticContent:  null,          // builder-only
};

const SPEC_TO_BUILDER_SECTION: Record<string, string> = {
  coverPage:            'cover',
  entityInfo:           'recipientBlock',
  responsibleParty:     'recipientBlock',
  customText:           'bodyText',
  inspectionInfo:       'metadata',
  complexVisitSummary:  'complexVisitSummary',
  complexParticipants:  'complexParticipants',
  violations:           'violations',
  photos:               'photos',
  inspectorNotes:       'comments',
  signatures:           'signatures',
  legalText:            'legal',
};

// ---------------------------------------------------------------------------
// toReportTemplate — convert builder DocumentConfig → ReportTemplateLocal fields
// Returns a partial that the caller merges with _id, name, status, etc.
// ---------------------------------------------------------------------------
export function builderConfigToReportTemplate(
  builderConfig: BuilderDocumentConfig,
): Omit<ReportTemplateLocal, '_id' | 'name' | 'description' | 'version' | 'status' | 'createdAt' | 'createdBy' | 'updatedAt' | 'updatedBy' | 'isDeleted'> {
  // Map documentType — builder allows more types; spec only has inspectionReport|permit
  const docType = mapDocumentType(builderConfig.documentType);

  // Build spec sections array from builder sections
  const sections: ReportSection[] = [];
  let order = 0;
  for (const bs of builderConfig.sections) {
    const specType = BUILDER_TO_SPEC_SECTION[bs.id];
    if (specType) {
      const section = {
        id: bs.id,
        type: specType as ReportSection['type'],
        order: order++,
        enabled: bs.enabled,
        config: getSectionConfig(bs.id, builderConfig),
      } as ReportSection;
      if (specType !== 'complexVisitSummary' && specType !== 'complexParticipants') {
        section.title = bs.label;
      }
      sections.push(section);
    }
  }

  // Map builder header → spec HeaderConfig
  const header = mapBuilderHeader(builderConfig.header);

  // Map builder footerConfig → spec FooterConfig
  const footer = mapBuilderFooter(builderConfig.footerConfig);

  // Map builder signatures → spec SignaturesConfig
  const signatures = mapBuilderSignatures(builderConfig.signatures);

  // Map builder photoConfig → spec PhotoConfig
  const photoConfig = mapBuilderPhotoConfig(builderConfig.photoConfig);

  // Default auto-hide rules
  const autoHideRules: AutoHideRules = {
    hideViolationsIfNone: builderConfig.violationConfig?.conditionalDisplay === 'whenPresent',
    hidePhotosIfNone: true,
    hideNotesIfEmpty: true,
    hideQuestionsIfNone: true,
    hideCoverPageIfNoViolations: false,
  };

  // Map legal text
  const legalText = mapBuilderLegalText(builderConfig.legalText);

  // Map cover page
  const coverPage = mapBuilderCoverPage(builderConfig.coverPage);

  // Map permit config
  const permitConfig = mapBuilderPermitConfig(builderConfig.permitConfig);

  // Preserve full builder state for round-trip (UI-only fields).
  // documentType + themePreset are preserved here because the spec `documentType` is a
  // 2-value enum (mapDocumentType collapses the 6 builder types) and the spec has no
  // theme field — so without this bag both reset on reload (G-REHYDRATE-01).
  const builderConfigBag: Record<string, any> = {
    documentType: builderConfig.documentType,
    themePreset: builderConfig.themePreset,
    themeCustom: builderConfig.themeCustom,
    reportDataScope: builderConfig.reportDataScope ?? 'singleCase',
    complexVisitSummary: builderConfig.complexVisitSummary,
    complexParticipants: builderConfig.complexParticipants,
    layoutMode: builderConfig.layoutMode,
    approvalMode: builderConfig.approvalMode,
    header: builderConfig.header,
    recipientBlock: builderConfig.recipientBlock,
    subjectLine: builderConfig.subjectLine,
    salutation: builderConfig.salutation,
    bodyText: builderConfig.bodyText,
    metadata: builderConfig.metadata,
    violationConfig: builderConfig.violationConfig,
    comments: builderConfig.comments,
    contactBlock: builderConfig.contactBlock,
    staticContent: builderConfig.staticContent,
    // Preserve original section order and enabled state for builder UI
    builderSections: builderConfig.sections.map(({ id, label, description, enabled, pageBreakBefore }) => ({
      id, label, description, enabled, pageBreakBefore,
    })),
  };

  return {
    documentType: docType,
    reportDataScope: builderConfig.reportDataScope ?? 'singleCase',
    layoutVariant: `${builderConfig.pageSize}-${builderConfig.orientation}`,
    pageSize: builderConfig.pageSize,
    orientation: builderConfig.orientation,
    coverPage,
    header,
    sections,
    footer,
    signatures,
    photoConfig,
    autoHideRules,
    legalText,
    permitConfig: docType === 'permit' ? permitConfig : undefined,
    builderConfig: builderConfigBag,
  };
}

// ---------------------------------------------------------------------------
// fromReportTemplate — convert ReportTemplateLocal → builder DocumentConfig
// ---------------------------------------------------------------------------
export function reportTemplateToBuilderConfig(
  template: ReportTemplateLocal,
): BuilderDocumentConfig {
  // If we have builderConfig bag, restore from it (round-trip)
  const bc = template.builderConfig || {};

  // Restore sections — prefer builderConfig.builderSections if available
  const builderSections: BuilderSectionConfig[] = bc.builderSections
    ? (bc.builderSections as BuilderSectionConfig[])
    : buildSectionsFromSpec(template);

  return {
    // Prefer the round-tripped builder documentType (full 6-value union); fall back to the
    // collapsed spec value only for templates saved before the bag carried it (G-REHYDRATE-01).
    documentType: (bc.documentType as BuilderDocumentConfig['documentType']) ?? (template.documentType as BuilderDocumentConfig['documentType']),
    themePreset: bc.themePreset as string | undefined,
    themeCustom: bc.themeCustom as BuilderDocumentConfig['themeCustom'],
    reportDataScope: (template.reportDataScope ?? bc.reportDataScope ?? 'singleCase') as ReportDataScope,
    layoutMode: bc.layoutMode || 'form',
    pageSize: template.pageSize,
    orientation: template.orientation,
    approvalMode: bc.approvalMode || 'requireApproval',
    sections: builderSections,
    header: reverseMapHeader(template.header, bc),
    recipientBlock: bc.recipientBlock,
    subjectLine: bc.subjectLine,
    salutation: bc.salutation,
    bodyText: bc.bodyText,
    metadata: bc.metadata,
    violationConfig: bc.violationConfig,
    photoConfig: reverseMapPhotoConfig(template.photoConfig, bc),
    comments: bc.comments,
    contactBlock: bc.contactBlock,
    signatures: reverseMapSignatures(template.signatures, bc),
    legalText: reverseMapLegalText(template.legalText, bc),
    footerConfig: reverseMapFooter(template.footer, bc),
    staticContent: bc.staticContent,
    coverPage: reverseMapCoverPage(template.coverPage, bc),
    permitConfig: reverseMapPermitConfig(template.permitConfig, bc),
    complexVisitSummary: bc.complexVisitSummary,
    complexParticipants: bc.complexParticipants,
  };
}

// ---------------------------------------------------------------------------
// Internal helpers — builder → spec
// ---------------------------------------------------------------------------

function mapDocumentType(builderType: string): DocumentType {
  if (builderType === 'permit') return 'permit';
  return 'inspectionReport';
}

function getSectionConfig(sectionId: string, bc: BuilderDocumentConfig): Record<string, any> | undefined {
  switch (sectionId) {
    case 'recipientBlock': return bc.recipientBlock;
    case 'bodyText': return bc.bodyText;
    case 'metadata': return bc.metadata;
    case 'violations': return bc.violationConfig;
    case 'photos': return bc.photoConfig;
    case 'comments': return bc.comments;
    case 'complexVisitSummary': return bc.complexVisitSummary;
    case 'complexParticipants': return bc.complexParticipants;
    case 'cover': return bc.coverPage;
    default: return undefined;
  }
}

function mapBuilderHeader(bh?: Record<string, any>): HeaderConfig {
  if (!bh) {
    return {
      showLogo: true, logoPosition: 'left', showDepartmentName: true,
      showDepartmentAddress: true, showDepartmentPhone: true,
      showReportTitle: true, showReportNumber: false, showDate: true,
      dateFormat: 'MM/DD/YYYY',
    };
  }
  return {
    showLogo: bh.showLogo ?? true,
    logoPosition: bh.logoPosition ?? 'left',
    showDepartmentName: bh.showDepartmentName ?? true,
    showDepartmentAddress: bh.showDepartmentAddress ?? true,
    showDepartmentPhone: bh.showDepartmentPhone ?? true,
    showReportTitle: true,
    reportTitle: bh.customTitle,
    customSubtitle: bh.customSubtitle,
    showReportNumber: false,
    showDate: true,
    dateFormat: 'MM/DD/YYYY',
  };
}

function mapBuilderFooter(bf?: Record<string, any>): FooterConfig {
  if (!bf) {
    return {
      showPageNumbers: true, pageNumberFormat: 'page-of-total',
      showDepartmentName: true, showConfidentialityNotice: false,
      showGeneratedDate: true,
    };
  }
  return {
    showPageNumbers: bf.showPageNumbers ?? true,
    pageNumberFormat: 'page-of-total',
    showDepartmentName: bf.showDepartmentInfo ?? true,
    showConfidentialityNotice: false,
    customFooterText: bf.customText,
    showGeneratedDate: true,
  };
}

function mapBuilderSignatures(bs?: Record<string, any>): SignaturesConfig {
  if (!bs) {
    return {
      inspectorSignature: { enabled: true, label: 'Inspector', showName: true, showTitle: true, showDate: true, showBadgeNumber: false },
      responsiblePartySignature: { enabled: false, label: 'Responsible Party', showName: true, showDate: true },
    };
  }
  return {
    inspectorSignature: {
      enabled: bs.inspectorSignature ?? true,
      label: 'Inspector',
      showName: bs.printedNameFields ?? true,
      showTitle: bs.showTitle ?? true,
      showDate: bs.dateFields ?? true,
      showBadgeNumber: false,
    },
    responsiblePartySignature: {
      enabled: bs.responsiblePartySignature ?? false,
      label: 'Responsible Party',
      showName: bs.printedNameFields ?? true,
      showDate: bs.dateFields ?? true,
    },
  };
}

function mapBuilderPhotoConfig(bp?: Record<string, any>): PhotoConfig {
  if (!bp) {
    return {
      placement: 'inline', showCaptions: true, showTimestamps: false,
      showLocation: false, photoSize: 'medium', photosPerRow: 2,
    };
  }
  // G-505: the builder's photoConfig.layout union is 'inline' | 'grid' | 'appendix'. 'gallery' is
  // kept for backward compatibility with templates persisted before the enum was reconciled.
  const placementMap: Record<string, PhotoConfig['placement']> = {
    inline: 'inline', grid: 'gallery', gallery: 'gallery', appendix: 'gallery',
  };
  return {
    placement: placementMap[bp.layout] ?? 'inline',
    maxGalleryPhotos: bp.maxPhotos,
    showCaptions: bp.showCaptions ?? true,
    showTimestamps: false,
    showLocation: false,
    photoSize: bp.thumbnailSize ?? 'medium',
    photosPerRow: 2,
  };
}

function mapBuilderLegalText(bl?: Record<string, any>): LegalTextConfig | undefined {
  if (!bl || !bl.enabled) return undefined;
  return {
    footerLegalText: bl.position === 'footer' || bl.position === 'both' ? bl.content : undefined,
    headerLegalText: bl.position === 'lastPage' || bl.position === 'both' ? bl.content : undefined,
  };
}

function mapBuilderCoverPage(bcp?: Record<string, any>): CoverPageConfig | undefined {
  if (!bcp) return undefined;
  return {
    enabled: bcp.enabled ?? false,
    showLogo: bcp.showLogo ?? true,
    logoSize: 'medium',
    showDepartmentName: true,
    showReportTitle: true,
    showEntityName: bcp.showEntityInfo ?? true,
    showEntityAddress: bcp.showEntityInfo ?? true,
    showInspectionDate: bcp.showInspectionSummary ?? true,
    showInspectorName: bcp.showInspectionSummary ?? true,
    showResultSummary: bcp.showResultBadge ?? true,
    resultSummaryStyle: 'badge',
  };
}

function mapBuilderPermitConfig(bpc?: Record<string, any>): PermitConfig | undefined {
  if (!bpc) return undefined;
  return {
    showPermitNumber: true,
    permitNumberPrefix: bpc.numberPrefix,
    showIssuanceDate: true,
    showExpirationDate: bpc.showExpirationDate ?? true,
    showConditions: bpc.showConditions ?? true,
    defaultConditions: [],
    showPaymentSummary: bpc.showPaymentSummary ?? false,
    showInspectionSummary: false,
  };
}

// ---------------------------------------------------------------------------
// Internal helpers — spec → builder (reverse mapping)
// ---------------------------------------------------------------------------

function buildSectionsFromSpec(template: ReportTemplateLocal): BuilderSectionConfig[] {
  // Rebuild builder sections from spec sections + defaults for missing ones
  const defaultOrder = [
    'cover', 'header', 'recipientBlock', 'subjectLine', 'salutation',
    'bodyText', 'complexVisitSummary', 'complexParticipants', 'metadata', 'violations', 'photos', 'comments',
    'contactBlock', 'signatures', 'legal', 'footer', 'staticContent',
  ];
  const defaultLabels: Record<string, string> = {
    cover: 'Cover Page', header: 'Header', recipientBlock: 'Recipient / Property',
    subjectLine: 'Subject Line', salutation: 'Salutation', bodyText: 'Body Text',
    complexVisitSummary: 'Complex Visit Summary', complexParticipants: 'Complex Participants',
    metadata: 'Inspection Metadata', violations: 'Violations', photos: 'Photos',
    comments: 'Comments', contactBlock: 'Contact Info', signatures: 'Signatures',
    legal: 'Legal Text', footer: 'Footer', staticContent: 'Static Attachment',
  };

  // Build a set of enabled spec section IDs mapped back to builder IDs
  const enabledBuilderIds = new Set<string>();
  for (const s of template.sections) {
    if (s.enabled) {
      const builderId = SPEC_TO_BUILDER_SECTION[s.type];
      if (builderId) enabledBuilderIds.add(builderId);
    }
  }
  // header and footer are top-level in spec, always "enabled" in builder
  enabledBuilderIds.add('header');
  enabledBuilderIds.add('footer');

  return defaultOrder.map((id) => ({
    id,
    label: defaultLabels[id] || id,
    description: '',
    enabled: enabledBuilderIds.has(id),
  }));
}

function reverseMapHeader(specHeader: HeaderConfig, bc: Record<string, any>): Record<string, any> {
  // Prefer builder's original header if available
  if (bc.header) return bc.header;
  return {
    showLogo: specHeader.showLogo,
    logoPosition: specHeader.logoPosition,
    showSecondLogo: false,
    secondLogoPosition: 'right',
    showDepartmentName: specHeader.showDepartmentName,
    showDepartmentAddress: specHeader.showDepartmentAddress,
    showDepartmentPhone: specHeader.showDepartmentPhone,
    showDepartmentFax: false,
    showDepartmentWebsite: false,
    customTitle: specHeader.reportTitle,
    customSubtitle: specHeader.customSubtitle,
  };
}

function reverseMapPhotoConfig(specPhoto: PhotoConfig, bc: Record<string, any>): Record<string, any> {
  if (bc.photoConfig) return bc.photoConfig;
  // G-505: the builder layout union is 'inline' | 'grid' | 'appendix', so a 'gallery' placement
  // maps back to 'grid' (the gallery-grid layout), not the removed 'gallery' value.
  const layoutMap: Record<string, string> = { inline: 'inline', gallery: 'grid', both: 'inline', none: 'inline' };
  return {
    layout: layoutMap[specPhoto.placement] || 'inline',
    maxPhotos: specPhoto.maxGalleryPhotos ?? 20,
    showCaptions: specPhoto.showCaptions,
    thumbnailSize: specPhoto.photoSize,
  };
}

function reverseMapSignatures(specSig: SignaturesConfig, bc: Record<string, any>): Record<string, any> {
  if (bc.signatures) return bc.signatures;
  return {
    style: 'full',
    inspectorSignature: specSig.inspectorSignature.enabled,
    responsiblePartySignature: specSig.responsiblePartySignature.enabled,
    witnessSignature: false,
    dateFields: specSig.inspectorSignature.showDate,
    printedNameFields: specSig.inspectorSignature.showName,
    showTitle: specSig.inspectorSignature.showTitle,
  };
}

function reverseMapLegalText(specLegal: LegalTextConfig | undefined, bc: Record<string, any>): Record<string, any> | undefined {
  if (bc.legalText) return bc.legalText;
  if (!specLegal) return { enabled: false, content: '', position: 'footer' };
  const content = specLegal.footerLegalText || specLegal.headerLegalText || '';
  return {
    enabled: !!content,
    content,
    position: specLegal.footerLegalText ? 'footer' : 'lastPage',
  };
}

function reverseMapFooter(specFooter: FooterConfig, bc: Record<string, any>): Record<string, any> {
  if (bc.footerConfig) return bc.footerConfig;
  return {
    showDepartmentInfo: specFooter.showDepartmentName,
    showPageNumbers: specFooter.showPageNumbers,
    customText: specFooter.customFooterText,
  };
}

function reverseMapCoverPage(specCover: CoverPageConfig | undefined, bc: Record<string, any>): Record<string, any> | undefined {
  if (bc.coverPage) return bc.coverPage;
  if (!specCover) return undefined;
  return {
    enabled: specCover.enabled,
    showLogo: specCover.showLogo,
    showEntityInfo: specCover.showEntityName,
    showInspectionSummary: specCover.showInspectionDate,
    showResultBadge: specCover.showResultSummary,
  };
}

function reverseMapPermitConfig(specPermit: PermitConfig | undefined, bc: Record<string, any>): Record<string, any> | undefined {
  if (bc.permitConfig) return bc.permitConfig;
  if (!specPermit) return undefined;
  return {
    numberPrefix: specPermit.permitNumberPrefix,
    showConditions: specPermit.showConditions,
    showPaymentSummary: specPermit.showPaymentSummary,
    showExpirationDate: specPermit.showExpirationDate,
    showRenewalInfo: false,
  };
}
